import { RefObject, useCallback, useEffect, useRef, useState } from "react";

/** How long the target row stays highlighted before returning to normal. */
export const FIND_HIGHLIGHT_DURATION_MS = 2000;

export type CompareColumnKey = "live" | "changes";

export interface FindHighlight {
  column: CompareColumnKey;
  id: string;
}

export interface UseFindCounterpartResult {
  /** Row currently highlighted as the find target, if any. */
  highlight: FindHighlight | undefined;
  /** Scrolls `targetColumn` to the row for `id` and highlights it. */
  findItem: (targetColumn: CompareColumnKey, id: string) => void;
}

const COLUMN_TESTID: Record<CompareColumnKey, string> = {
  live: "compare-changes-currently-live",
  changes: "compare-changes-changes",
};

/**
 * Find-counterpart interaction (GRWBKK-1767): locates the row for an item in
 * the opposite column by its stable item id, scrolls that column to center
 * it, and highlights it temporarily. Works in both directions — live rows
 * find their match in the changes column and vice versa. Repeated calls
 * retarget the highlight and restart the timer.
 */
export const useFindCounterpart = (
  bodyRef: RefObject<HTMLElement>,
): UseFindCounterpartResult => {
  const [highlight, setHighlight] = useState<FindHighlight>();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const findItem = useCallback(
    (targetColumn: CompareColumnKey, id: string) => {
      const target = bodyRef.current?.querySelector(
        `[data-testid="compare-${targetColumn}-row-${id}"]`,
      );
      const column = bodyRef.current?.querySelector(
        `[data-testid="${COLUMN_TESTID[targetColumn]}"]`,
      );
      if (target && column) {
        // Scroll only the target column — scrollIntoView would also shift
        // scrollable (or overflow-hidden) ancestors like the modal body.
        const columnRect = column.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        column.scrollTo({
          top:
            column.scrollTop +
            (targetRect.top - columnRect.top) -
            (column.clientHeight - targetRect.height) / 2,
          behavior: "smooth",
        });
      }

      setHighlight({ column: targetColumn, id });
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setHighlight(undefined);
      }, FIND_HIGHLIGHT_DURATION_MS);
    },
    [bodyRef],
  );

  return { highlight, findItem };
};
